<template>
  <Node
    class="approvers"
    :class="{ 'tw-cursor-move': dragging }"
    data-test="workflow-approver-groups"
    @delete="$emit('delete')"
  >
    <template #icon>
      <WdsSpinner v-if="loading" indeterminate />
      <WdsIcon v-else thin name="checkmark-outline" :size="20" />
    </template>
    <template #title>
      <WdsText large>{{ $t('Approved by') }}</WdsText>
    </template>
    <div class="tw-mt-3">
      <TransitionGroup name="list">
        <div
          v-for="(group, index) in allGroups"
          :key="group.id"
          class="tw-mb-4"
          @dragover="dragOver(group, $event)"
        >
          <WdsStack gap-sm align-middle justify-center>
            <WdsStack
              class="drag-row"
              :draggable="allGroups.length > 1"
              gap-sm
              align-middle
              justify-center
              :class="{ 'active-drag': dragged?.id === group.id }"
              @dragstart="dragStart(group)"
              @dragend="dragEnd"
            >
              <div v-if="allGroups.length > 1" class="dragger">
                <WdsIcon :size="16" color="#B2B2BF" name="draggable" />
              </div>
              <WdsSelect
                v-model="group.userGroup"
                :data-test="`approval-group-select-${index}`"
                :placeholder="$t('Select')"
                class="select-w-full"
                :list="userGroups"
                name="workflow-user-group-select"
                @load-more="loadMoreUserGroups"
              >
                <template v-if="allGroups.length > 1" #prefix>
                  <div class="prefix-order" :class="{ 'overflow-border': index !== 0 }">
                    {{ index + 1 }}
                  </div>
                </template>
                <template #header="{ option }">
                  <WdsStack v-if="option" align-center justify-space-between class="tw-w-full">
                    <WdsText class="truncate">{{ option.label }}</WdsText>
                    <WdsStack gap-sm align-center>
                      <WdsAvatarGroup
                        sm
                        :avatars="
                          option?.users?.data.map((u) => ({ ...u, path: u.avatarPath })) ?? []
                        "
                        :total="option?.users?.paginatorInfo.total"
                      />
                    </WdsStack>
                  </WdsStack>
                </template>
                <template #option="{ option }">
                  <div
                    v-if="option.value === newGroupSelectOption.value"
                    class="new-field-option"
                    @mousedown.stop="openCreateGroupModal"
                  >
                    <WdsStack class="option-label" gap-sm>
                      <WdsIcon :size="20" name="plus" />
                      {{ option.label }}
                    </WdsStack>
                  </div>
                  <WdsStack
                    v-else
                    :data-test="`user-group-option-${option.value}`"
                    align-center
                    justify-space-between
                    class="tw-w-full"
                  >
                    <WdsText class="truncate">{{ option.label }}</WdsText>
                    <WdsStack gap-sm align-center>
                      <WdsAvatarGroup
                        sm
                        :avatars="
                          option?.users?.data.map((u) => ({ ...u, path: u.avatarPath })) ?? []
                        "
                        :total="option?.users?.paginatorInfo.total"
                      />
                    </WdsStack>
                  </WdsStack>
                </template>
              </WdsSelect>
            </WdsStack>
            <WdsButtonIcon
              v-if="allGroups.length > 1"
              sm
              transparent-background
              @click="removeGroup(group)"
            >
              <WdsIcon :size="14" color="#B2B2BF" name="close" />
            </WdsButtonIcon>
          </WdsStack>
        </div>
        <WdsButton
          key="add-approval-step"
          tertiary
          full-width
          data-test="add-approval-step-button"
          :disabled="loading"
          @click="addGroup"
        >
          {{ $t('Add approval step') }}
        </WdsButton>
      </TransitionGroup>
    </div>
  </Node>

  <teleport to="body">
    <WdsModal
      :title="$t('Create group')"
      :show="showNewGroupModal"
      @cancel="showNewGroupModal = false"
    >
      <ApprovalGroupEditor @cancel="showNewGroupModal = false" @submitted="submitted" />
    </WdsModal>
  </teleport>
</template>

<script setup lang="ts">
import {
  WdsText,
  WdsStack,
  WdsButtonIcon,
  WdsIcon,
  WdsSelect,
  WdsButton,
  WdsSpinner,
  WdsAvatarGroup,
  WdsModal,
} from '@worksome/wds-components'
import { useQuery } from '@vue/apollo-composable'
import { computed, Ref, ref } from 'vue'
import { useVModel } from '@vueuse/core'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { flat, uuid } from '@/utils/tools'
import Node from '@/components/Workflows/Pages/Editor/Nodes/NodeComponent.vue'
import { userGroupsQuery } from '@/components/Workflows/Pages/Groups/graphql/queries'
import ApprovalGroupEditor from '@/components/Workflows/Pages/Groups/GroupEditor.vue'
import { NetworkError } from '@/utils/notification'
import { Group, UserGroupItems, UserGroup } from '@/components/Workflows/Pages/Workflow/workflow'
import { useFragment } from '@/gql'
import {
  UserGroupFragment,
  UsersFragment,
} from '@/components/Workflows/Pages/Groups/graphql/fragment'
import { UserGroupStatus } from '@/gql/graphql'

const emit = defineEmits(['delete', 'update:modelValue'])

// ================= Basic data start =================
const newGroupSelectOption = {
  label: $t('New group'),
  value: 'new_group',
  // Appeasing the ts gods
  id: '',
  name: '',
  description: '',
  status: UserGroupStatus.Inactive,
} as UserGroupItems

const props = withDefaults(
  defineProps<{
    modelValue?: Group[]
  }>(),
  {
    modelValue: () => [],
  },
)

const showNewGroupModal = ref(false)

const openCreateGroupModal = () => {
  showNewGroupModal.value = true
}

const groups: Ref<Group[]> = useVModel(props, 'modelValue', emit, {
  passive: true,
  deep: true,
})

function createGroupItem() {
  return {
    id: uuid(),
    userGroup: null,
    position: -1, // We only care about the position when saving happens. This is only to appease the TS gods
  }
}

function addGroup() {
  groups.value.push(createGroupItem())
}

function removeGroup(group: Group) {
  groups.value = groups.value.filter((r) => r.id !== group.id)
}

const allGroups = computed(() => {
  if (groups.value.length === 0) {
    addGroup() // Adding a group prevents double clicking of the add conditional button
  }
  return groups.value
})

// ================= Basic data end =================

// ================= Dragging logic start =================
const dragged = ref<Group | null>(null)
const dragging = computed(() => dragged.value !== null)
const groupInAnimation = ref<Group | null>(null)

const dragEnd = () => {
  dragged.value = null
  groupInAnimation.value = null
}

const dragStart = async (group: Group) => {
  dragged.value = group
}

const dragOver = (group: Group, event: DragEvent) => {
  event.preventDefault()
  if (!dragging.value) return

  const data = dragged.value?.id

  if (group.id !== data && groupInAnimation.value?.id !== group.id) {
    const orgIndex = groups.value.findIndex((g) => g.id === dragged.value?.id)
    const newIndex = groups.value.findIndex((g) => g.id === group.id)

    groups.value.splice(orgIndex, 1)
    groups.value.splice(newIndex, 0, dragged.value as Group)
  }
  groupInAnimation.value = group
}

// ================= Dragging logic end =================

// ================= Query logic start =================
const { result, loading, fetchMore, refetch } = useQuery(
  userGroupsQuery,
  {
    accounts: [currentAccount.globalId],
    search: '',
    groupFirst: 50,
    groupPage: 1,
    usersFirst: 10,
    usersPage: 1,
  },
  {
    clientId: 'publicApiCustomFields',
  },
)

const loadMoreUserGroups = () => {
  if (loading.value) return
  const { hasMorePages, currentPage } = flat(result.value ?? {}) ?? {}
  if (!hasMorePages) return
  fetchMore({
    variables: {
      groupPage: currentPage + 1,
    },
  })
}

function submitted() {
  try {
    refetch()
  } catch (ex: any) {
    new NetworkError({
      ex,
      msg: $t('An error occurred retrieving the new group, try and reload the page.'),
    }).dispatch()
  }
  showNewGroupModal.value = false
}

let oldState: UserGroupItems[] = [newGroupSelectOption]
const userGroups = computed<UserGroupItems[]>(() => {
  if (loading.value) {
    return oldState.concat(
      groups.value
        .map((g) => g.userGroup)
        .filter((ug): ug is Required<UserGroup> => ug !== null)
        .map((ug) => ({
          value: ug.id,
          label: ug.name,
          ...ug,
        }))
        .filter((gi: UserGroupItems) => !oldState.find((og) => og.id === gi.id)),
    )
  }

  const userGroupListItems =
    result.value?.userGroups.data
      .map(
        (group) =>
          ({
            ...useFragment(UsersFragment, group),
            ...useFragment(UserGroupFragment, group),
          }) as UserGroup,
      )
      .map((group) => {
        const item: UserGroupItems = {
          value: group.id,
          label: group.name,
          ...group,
        }

        return item
      }) ?? []

  oldState = [newGroupSelectOption].concat(userGroupListItems)
  oldState = [...new Map(oldState.map((item) => [item.value, item])).values()]
  return oldState
})
// ================= Query logic end =================
</script>

<style lang="scss" scoped>
.approvers {
  .drag-row {
    flex: 1;
    min-width: 0; // Fixes some flex space overflow issues

    &.active-drag {
      opacity: 0.5;
      .overflow-border {
        &::before {
          display: none; // During dragging the border causes a blocking effect which looks bad this removes that.
        }
      }
    }

    .dragger {
      display: flex;
      align-items: center;
      cursor: move;
    }
  }

  .truncate {
    min-width: 0;
    ::v-deep(div) {
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
    }
  }

  .prefix-order {
    width: 1.5rem;
    height: 1.5rem;

    line-height: 1.5rem;
    text-align: center;

    background-color: #f2f2f2;
    color: black;
    border-radius: 6px;
    &.overflow-border {
      position: relative;
      &::before {
        display: inline;
        content: '';
        width: 0;
        height: 16px;
        border-left: dotted 2px var(--border-color);
        position: absolute;
        top: calc(
          -36px // The height of the row
          + 1rem // The space in the gap
          - 2px // The width of the border
        );
        left: 50%;
        margin-left: -1px;
      }
    }
  }

  .select-w-full {
    min-width: 0;
    flex: 1;
    &::v-deep(.overflow-text) {
      max-width: 100%;
      width: 100%;
      overflow: hidden;
    }
  }

  .divider-text {
    &::v-deep(.wds-text-tertiary) {
      font-size: 12px;
    }
  }

  .list-move, /* apply transition to moving elements */
  .list-leave-active,
  .list-enter-active {
    transition: all 0.5s ease;
  }

  .list-enter-from,
  .list-leave-to {
    opacity: 0;
    transform: translateX(30px);
  }

  /* ensure leaving items are taken out of layout flow so that moving
     animations can be calculated correctly. */
  .list-leave-active {
    position: absolute;
    width: 100%;
  }
}
</style>
